import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Link,
  Divider,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import {
  brand,
  pageHeroRichSxFor,
  pageHeroImages,
  pageHeroType,
  contactHeroMinHeight,
  COMPANY_PHONE,
  COMPANY_PHONE_TEL,
  COMPANY_ADDRESS_LINES,
  COMPANY_EMAIL_LINES,
} from '../brandTheme';

const CONTACT_ROWS = [
  { icon: <PhoneIcon />, title: 'Call Us', lines: [COMPANY_PHONE], href: COMPANY_PHONE_TEL },
  { icon: <LocationOnIcon />, title: 'Visit Us', lines: [...COMPANY_ADDRESS_LINES] },
  {
    icon: <EmailIcon />,
    title: 'Email Us',
    lines: [...COMPANY_EMAIL_LINES],
  },
  { icon: <LanguageIcon />, title: 'Website', lines: ['www.omsaifin.com'], href: 'https://www.omsaifin.com' },
];

const FAQS = [
  { q: 'How long does approval take?', a: 'Most approvals are processed within 24–48 hours after complete documents are received.' },
  { q: 'Do you support digital documentation?', a: 'Yes, we support secure digital KYC and document uploads for faster processing.' },
  { q: 'What interest rates can I expect?', a: 'Rates depend on your profile and product. We match you with competitive options.' },
];

/** One channel tile — links where applicable */
function ChannelTile({ item }) {
  const tileSx = {
    p: 2.75,
    height: '100%',
    minHeight: { xs: 'auto', md: 200 },
    borderRadius: '22px',
    textAlign: 'center',
    bgcolor: '#fff',
    border: `1px solid ${brand.border}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '&:hover': {
      borderColor: brand.primary,
      boxShadow: '0 16px 40px rgba(15, 118, 110, 0.12)',
      transform: 'translateY(-4px)',
    },
  };

  const iconWrap = (
    <Box
      sx={{
        width: 56,
        height: 56,
        mb: 2,
        borderRadius: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, rgba(15, 118, 110, 0.12) 0%, rgba(55, 48, 163, 0.1) 100%)`,
        color: brand.primary,
        '& svg': { fontSize: 28 },
      }}
    >
      {item.icon}
    </Box>
  );

  const label = (
    <Typography sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.16em', color: brand.muted, mb: 1.25 }}>
      {item.title.toUpperCase()}
    </Typography>
  );

  const lines = item.lines.map((line, i) => {
    const isEmail = item.title === 'Email Us';
    const mailto = isEmail ? `mailto:${line}` : undefined;
    const body = (
      <Typography
        sx={{
          color: brand.slate,
          fontWeight: item.title === 'Call Us' ? 800 : 600,
          fontSize: item.title === 'Call Us' ? '1.15rem' : '0.9375rem',
          lineHeight: 1.5,
        }}
      >
        {line}
      </Typography>
    );
    if (mailto) {
      return (
        <Link key={i} href={mailto} underline="hover" sx={{ color: 'inherit', display: 'block', mb: 0.5 }}>
          {body}
        </Link>
      );
    }
    if (item.href && item.title === 'Website') {
      return (
        <Link key={i} href={item.href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: brand.primary }}>
          {body}
        </Link>
      );
    }
    return (
      <Typography key={i} component="div" sx={{ color: brand.muted, fontSize: '0.9375rem', lineHeight: 1.55 }}>
        {line}
      </Typography>
    );
  });

  const body = (
    <Box sx={tileSx}>
      {iconWrap}
      {label}
      <Box sx={{ width: '100%' }}>{lines}</Box>
      {item.href && item.title === 'Call Us' && (
        <Typography component="span" sx={{ mt: 'auto', pt: 2, fontSize: '0.8125rem', fontWeight: 700, color: brand.primary }}>
          Tap to call →
        </Typography>
      )}
    </Box>
  );

  if (item.href && item.title === 'Call Us') {
    return (
      <Link href={item.href} underline="none" sx={{ color: 'inherit', display: 'block', height: '100%' }}>
        {body}
      </Link>
    );
  }

  return body;
}

const Contact = () => {
  const heroBase = pageHeroRichSxFor(pageHeroImages.contact);
  const heroSx = {
    ...heroBase,
    minHeight: contactHeroMinHeight,
    py: { xs: 12, md: 18 },
  };

  return (
    <Box id="contact" sx={{ bgcolor: brand.surfaceWarm, pb: 0 }} className="component-box component-box--flush">
      <Box sx={{ ...heroSx, textAlign: 'center' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography component="p" sx={{ ...pageHeroType.eyebrow, mb: 2.5, textAlign: 'center' }}>
            CONTACT
          </Typography>
          <Typography component="h1" sx={{ ...pageHeroType.title, mb: 2.5, textAlign: 'center', fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' } }}>
            Get In <Box component="span" sx={{ color: '#fde68a' }}>Touch</Box>
          </Typography>
          <Typography sx={{ ...pageHeroType.subtitle, maxWidth: 680, mx: 'auto', textAlign: 'center', mb: 4 }}>
            Ready to transform your financial future? We are just a message away.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button
              component="a"
              href={COMPANY_PHONE_TEL}
              variant="contained"
              size="large"
              startIcon={<PhoneIcon />}
              sx={{
                borderRadius: '999px',
                px: 3.5,
                py: 1.5,
                fontWeight: 800,
                fontSize: '0.9375rem',
                bgcolor: '#fbbf24',
                color: '#422006',
                boxShadow: '0 12px 36px rgba(0,0,0,0.28)',
                '&:hover': { bgcolor: '#fcd34d' },
              }}
            >
              {COMPANY_PHONE}
            </Button>
            <Button
              component="a"
              href={`mailto:${COMPANY_EMAIL_LINES[0]}`}
              variant="outlined"
              size="large"
              sx={{
                borderRadius: '999px',
                px: 3.5,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.9375rem',
                borderWidth: 2,
                borderColor: 'rgba(255,255,255,0.55)',
                color: '#fff',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Email us
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Unified hub below hero — new layout */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: { xs: -9, md: -12 },
          pb: { xs: 8, md: 12 },
          px: { xs: 2, sm: 2.5, md: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: '26px', md: '40px' },
              overflow: 'hidden',
              bgcolor: '#fff',
              border: `1px solid ${brand.border}`,
              boxShadow: '0 40px 100px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(255,255,255,0.6) inset',
            }}
          >
            <Box
              sx={{
                height: 8,
                background: `linear-gradient(90deg, ${brand.primary} 0%, ${brand.indigo} 38%, #d97706 72%, ${brand.primaryLight} 100%)`,
              }}
            />

            <Box
              sx={{
                py: { xs: 3.5, md: 4.5 },
                px: 3,
                textAlign: 'center',
                background: `linear-gradient(165deg, ${brand.surfaceWarm} 0%, #ffffff 55%)`,
                borderBottom: `1px solid ${brand.border}`,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.65rem' }, color: brand.slate, letterSpacing: '-0.02em' }}>
                Choose a channel
              </Typography>
              <Typography sx={{ mt: 1.25, color: brand.muted, fontSize: { xs: '1rem', md: '1.0625rem' }, maxWidth: 560, mx: 'auto', lineHeight: 1.65 }}>
                Same team whether you call, write, or walk in — Thane West.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
              {CONTACT_ROWS.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <ChannelTile item={item} />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: brand.border }} />

            <Grid container sx={{ p: { xs: 2.5, sm: 3, md: 4 } }} spacing={{ xs: 3, md: 4 }} alignItems="stretch">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 3.5 },
                    borderRadius: '24px',
                    bgcolor: brand.surfaceWarm,
                    border: `1px solid ${brand.border}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 5,
                      height: '100%',
                      background: `linear-gradient(180deg, ${brand.primary}, ${brand.indigo})`,
                      borderRadius: '24px 0 0 24px',
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, pl: 2 }}>
                    <MapOutlinedIcon sx={{ color: brand.primary, fontSize: 28 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: brand.slate }}>Visit us</Typography>
                  </Stack>
                  {COMPANY_ADDRESS_LINES.map((line, i) => (
                    <Typography key={i} sx={{ color: brand.muted, fontSize: '1.02rem', lineHeight: 1.65, pl: 2 }}>
                      {line}
                    </Typography>
                  ))}
                  <Button
                    component="a"
                    href="https://goo.gl/maps/placeholder"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    fullWidth
                    sx={{
                      mt: 3,
                      py: 1.25,
                      borderRadius: '14px',
                      fontWeight: 700,
                      borderColor: brand.primary,
                      color: brand.primary,
                      '&:hover': { borderColor: brand.primaryDark, bgcolor: 'rgba(15, 118, 110, 0.06)' },
                    }}
                  >
                    Open in Maps
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 4 },
                    borderRadius: '24px',
                    bgcolor: '#f8fafc',
                    border: `1px dashed ${brand.borderStrong}`,
                  }}
                >
                  <Typography sx={{ color: brand.primary, fontWeight: 800, letterSpacing: '0.18em', fontSize: '0.72rem', mb: 1.5 }}>
                    WRITE TO US
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: brand.slate, fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '-0.02em', mb: 1 }}>
                    Send a message
                  </Typography>
                  <Typography sx={{ color: brand.muted, fontSize: '1.0625rem', lineHeight: 1.65, mb: 3.5, maxWidth: 520 }}>
                    Share your query and we&apos;ll get back to you with clear next steps.
                  </Typography>
                  <form noValidate autoComplete="off">
                    <Grid container spacing={2.5}>
                      {[
                        { label: 'Name', width: 6 },
                        { label: 'Email', width: 6 },
                        { label: 'Subject', width: 12 },
                        { label: 'Message', width: 12, multiline: true, rows: 5 },
                      ].map((field, idx) => (
                        <Grid item xs={12} sm={field.width} key={idx}>
                          <TextField
                            label={field.label}
                            variant="outlined"
                            fullWidth
                            multiline={field.multiline}
                            rows={field.rows}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '14px',
                                bgcolor: '#fff',
                                fontSize: '1rem',
                                '& fieldset': { borderColor: brand.border },
                                '&:hover fieldset': { borderColor: brand.borderStrong },
                                '&.Mui-focused fieldset': { borderColor: brand.primary, borderWidth: 2 },
                              },
                              '& .MuiInputLabel-root': { fontSize: '1rem' },
                              '& .MuiInputLabel-root.Mui-focused': { color: brand.primary },
                            }}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          endIcon={<SendIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            alert('Thank you for your message! We will get back to you soon.');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          sx={{
                            py: 2,
                            fontSize: '1.0625rem',
                            fontWeight: 800,
                            borderRadius: '999px',
                            letterSpacing: '0.04em',
                            background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)`,
                            boxShadow: '0 16px 40px rgba(15, 118, 110, 0.35)',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 22px 48px rgba(15, 118, 110, 0.4)',
                            },
                            transition: 'all 0.25s ease',
                          }}
                        >
                          Send message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* FAQ + hours */}
          <Paper
            elevation={0}
            sx={{
              mt: { xs: 5, md: 6 },
              borderRadius: '28px',
              overflow: 'hidden',
              border: `1px solid ${brand.border}`,
              boxShadow: '0 28px 72px rgba(15, 23, 42, 0.09)',
              bgcolor: '#fff',
            }}
          >
            <Grid container>
              <Grid item xs={12} md={8} sx={{ p: { xs: 3.5, md: 5 } }}>
                <Typography sx={{ color: brand.indigo, fontWeight: 800, letterSpacing: '0.18em', fontSize: '0.75rem', mb: 2 }}>
                  SUPPORT
                </Typography>
                <Typography sx={{ fontWeight: 800, color: brand.slate, fontSize: { xs: '1.5rem', md: '1.85rem' }, letterSpacing: '-0.02em', mb: 4 }}>
                  Frequently asked questions
                </Typography>
                <Stack spacing={2.5}>
                  {FAQS.map((faq, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 3,
                        borderRadius: '18px',
                        bgcolor: brand.surfaceWarm,
                        borderLeft: `5px solid ${index === 0 ? brand.primary : index === 1 ? brand.indigo : brand.accent}`,
                        border: `1px solid ${brand.border}`,
                        borderLeftWidth: 5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: brand.slate, fontSize: '1.08rem', mb: 1.25, lineHeight: 1.35 }}>
                        {faq.q}
                      </Typography>
                      <Typography sx={{ color: brand.muted, lineHeight: 1.75, fontSize: '1.02rem' }}>
                        {faq.a}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  background: 'linear-gradient(160deg, #1e1b4b 0%, #312e81 40%, #0f766e 100%)',
                  color: '#fff',
                  p: { xs: 4, md: 5 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle at 80% 20%, rgba(251,191,36,0.15) 0%, transparent 45%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '14px',
                        bgcolor: 'rgba(255,255,255,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccessTimeIcon sx={{ color: '#fde68a' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.35rem' }}>Office hours</Typography>
                  </Stack>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.0625rem', mb: 1.5, lineHeight: 1.6 }}>
                    Mon–Sat: 9:30 AM – 7:00 PM
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.0625rem', mb: 4, lineHeight: 1.6 }}>
                    Sun: On Appointment
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    href="https://goo.gl/maps/placeholder"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mb: 2,
                      py: 1.35,
                      color: '#fde68a',
                      borderColor: 'rgba(253,230,138,0.7)',
                      borderRadius: '999px',
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      borderWidth: 2,
                      '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    View on Google Maps
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    href={COMPANY_PHONE_TEL}
                    sx={{
                      py: 1.5,
                      bgcolor: '#fbbf24',
                      color: '#422006',
                      fontWeight: 800,
                      fontSize: '0.9375rem',
                      borderRadius: '999px',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                      '&:hover': { bgcolor: '#fcd34d' },
                    }}
                  >
                    Call now
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
